(function () {
  const initializedVideos = new WeakSet();
  const trackedVideos = new Set();
  let visibilityBound = false;
  let pageShowBound = false;
  let loadBound = false;
  let observer;

  function markBlocked(video) {
    video.classList.add('hero-bg-video--blocked');
  }

  function clearBlocked(video) {
    video.classList.remove('hero-bg-video--blocked');
  }

  function shouldDeferVideo(video) {
    return video?.dataset.heroDeferUntilLoad === 'true' && document.readyState !== 'complete';
  }

  function activateSources(video) {
    if (!video || video.dataset.heroSourcesActivated === 'true') return;

    video.querySelectorAll('source[data-src]').forEach(function (source) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.heroSourcesActivated = 'true';
  }

  function primeVideo(video) {
    const deferUntilLoad = shouldDeferVideo(video);

    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.playsInline = true;
    video.autoplay = !deferUntilLoad;
    video.loop = true;
    video.controls = false;
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;

    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (deferUntilLoad) {
      video.removeAttribute('autoplay');
    } else {
      video.setAttribute('autoplay', '');
    }
    video.setAttribute('loop', '');
    video.setAttribute('preload', video.getAttribute('preload') || 'auto');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('tabindex', '-1');
    video.setAttribute('x-webkit-airplay', 'deny');
    video.removeAttribute('controls');
  }

  function tryPlay(video) {
    if (!video || shouldDeferVideo(video)) {
      return;
    }

    activateSources(video);

    if (video.readyState === 0) {
      try {
        video?.load?.();
      } catch (_) {}
    }

    primeVideo(video);

    if (!video.paused && !video.ended) {
      clearBlocked(video);
      return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise
        .then(() => {
          clearBlocked(video);
        })
        .catch(() => {
          markBlocked(video);
        });
    }
  }

  function bindVideo(video) {
    if (initializedVideos.has(video)) {
      tryPlay(video);
      return;
    }

    initializedVideos.add(video);
    trackedVideos.add(video);
    primeVideo(video);

    const onPlayable = function () {
      tryPlay(video);
    };

    video.addEventListener('loadeddata', onPlayable, { passive: true });
    video.addEventListener('canplay', onPlayable, { passive: true });
    video.addEventListener('playing', function () {
      clearBlocked(video);
    }, { passive: true });
    video.addEventListener('error', function () {
      markBlocked(video);
    }, { passive: true });

    const deferUntilLoad = shouldDeferVideo(video);

    if ('IntersectionObserver' in window) {
      if (!observer) {
        observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                tryPlay(entry.target);
              }
            });
          },
          { threshold: 0.15 }
        );
      }

      observer.observe(video);
    }

    if (!visibilityBound) {
      visibilityBound = true;
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) {
          trackedVideos.forEach(tryPlay);
        }
      });
    }

    if (!pageShowBound) {
      pageShowBound = true;
      window.addEventListener('pageshow', function () {
        trackedVideos.forEach(tryPlay);
      });
    }

    if (!loadBound) {
      loadBound = true;
      window.addEventListener('load', function () {
        trackedVideos.forEach(tryPlay);
      });
    }

    if (deferUntilLoad) {
      return;
    }

    if (video.readyState >= 2) {
      tryPlay(video);
    } else {
      try {
        video.load();
      } catch (_) {}
    }
  }

  function initHeroVideos(root) {
    const scope = root || document;
    const videos = scope.querySelectorAll('[data-hero-autoplay-video]');

    videos.forEach(function (video) {
      bindVideo(video);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initHeroVideos();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initHeroVideos(event.target);
  });

  if (document.readyState !== 'loading') {
    initHeroVideos();
  }
})();
